import { useParams } from "react-router-dom";
import { useGetCsvLogByIdAdminHttp } from "../../../../hooks/http/admin/favorites/get-csv-log-by-id.admin.http";
import { handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import { useEffect, useState } from "react";
import Spinner from "../../../../components/spinner.components";
import DocumentIcon from "../../../../assets/svgs/common/document.svg";
import type { ICSVUpload } from "../../../../hooks/http/admin/favorites/add-new-csv-favorite-collection.admin.http";
import { DateTime } from "luxon";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../../../components/ui/tabs";
import {
	type IFavoriteSocialCsv,
	useGetAllUploadLogsAdminHttp,
} from "../../../../hooks/http/admin/favorites/get-all-upload-logs-for-csv.admin.http";
import { RetryAll } from "../../../../components/action-buttons/favorites/retry-failed-button.components";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import { Button } from "../../../../components/ui/button";
import { useDownloadCsvUploadDataAdminHttp } from "../../../../hooks/http/admin/favorites/download-csv-upload-data.admin.http";
import DownloadIcon2 from "../../../../assets/svgs/common/download-2.svg";

export const CsvSliderMenuAdminPartial: React.FunctionComponent = () => {
	const {
		execute: getCsvLogById,
		fetching: fetchingCsvLogById,
		cancel: cancelGetCsvLogById,
	} = useGetCsvLogByIdAdminHttp();
	const {
		execute: getAllUploadLogHttp,
		fetching: fetchingAllUploadLogs,
		cancel: cancelGetAllUploadLogs,
	} = useGetAllUploadLogsAdminHttp();

	const {
		execute: downloadCsvUploadDataHttp,
		fetching: fetchingDownloadCsvUploadData,
	} = useDownloadCsvUploadDataAdminHttp();
	const [csvData, setCsvData] = useState<ICSVUpload>();
	const [tab, setTab] = useState("success");
	const [uploadLogs, setUploadLogs] = useState<IFavoriteSocialCsv[]>([]);

	const { csvId } = useParams();

	const { toast } = useToast();

	async function getAllUploadLogs() {
		if (!csvId) {
			return;
		}
		cancelGetAllUploadLogs();
		const response = await getAllUploadLogHttp({
			body: null,
			pathParams: {
				csvId,
			},
			queryParams: {
				status: tab,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setUploadLogs(response.data.csvLogs);
	}

	async function getCsvLog() {
		if (!csvId) {
			return;
		}
		cancelGetCsvLogById();
		const response = await getCsvLogById({
			body: null,
			pathParams: {
				csvId,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setCsvData(response.data.csvLog);
	}

	async function downloadCsv() {
		if (!csvId) {
			return;
		}
		const response = await downloadCsvUploadDataHttp({
			body: null,
			pathParams: {
				csvId,
			},
			queryParams: {
				status: tab,
			},
		});

		response.ignoreError = true;

		if (!response.success) {
			return handleHttpError(response, toast);
		}

		const url = response.data.url;
		const link = document.createElement("a");
		link.href = url;
		link.click();
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getCsvLog();
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getAllUploadLogs();
	}, [tab]);

	return (
		<div className="relative flex flex-col justify-center items-center w-full h-full">
			{fetchingCsvLogById ? (
				<Spinner />
			) : (
				<div className="flex flex-col w-full h-full">
					<div className="flex items-center gap-3">
						<DocumentIcon />
						<div className="flex flex-col text-start">
							<p className="font-semibold text-2xl">{csvData?.title}</p>
						</div>
					</div>
					<div className="flex justify-between items-center pt-14 pr-10">
						<div className="flex flex-col flex-1">
							<span className="text-base text-secondary">Date</span>
							<p className="flex items-center text-base">
								{DateTime.fromISO(
									csvData?.createdAt?.toString() || "",
								).toFormat("dd-MM-yyyy")}
								<span className="pl-3 text-secondary text-sm">
									{DateTime.fromISO(
										csvData?.createdAt?.toString() || "",
									).toFormat(" hh:mm a")}
								</span>
							</p>
						</div>
						<div className="border border-border h-full" />
						<div className="flex flex-col flex-1 items-end text-left">
							<div className="flex flex-col">
								<span className="text-base text-secondary">
									Name of uploader
								</span>
								<p className="text-xl">{csvData?.createdBy.name}</p>
							</div>
						</div>
					</div>
					<div className="flex flex-col pt-16 w-full">
						<Tabs
							className="w-full"
							value={tab}
							onValueChange={(value) => {
								setTab(value);
							}}
						>
							<TabsList className="w-full">
								<TabsTrigger className="flex-1" value="success">
									Success list
								</TabsTrigger>
								<TabsTrigger className="flex-1" value="failed">
									Failed
								</TabsTrigger>
							</TabsList>

							<TabsContent value="success">
								<div className="flex flex-col gap-4 pt-10 w-full">
									<div className="flex justify-between items-center w-full">
										<p className="text-secondary text-xs">User names</p>
										<Button
											disabled={fetchingDownloadCsvUploadData}
											onClick={downloadCsv}
											className="flex items-center bg-transparent font-semibold underline"
										>
											<DownloadIcon2 />
											Download CSV
										</Button>
									</div>

									{fetchingAllUploadLogs ? (
										<div className="flex flex-col justify-center items-center pt-28 w-full h-96">
											<Spinner />
										</div>
									) : (
										<div className="flex flex-col w-full">
											<div className="gap-8 grid grid-cols-1 overflow-y-scroll">
												{uploadLogs.map((uploadLog, index) => (
													<div key={uploadLog.id} className="flex flex-col">
														<div className="flex justify-between items-center">
															<p>{uploadLog.handle}</p>
														</div>
														{uploadLogs.length - 1 !== index && (
															<div className="pt-4 pb-4 border-b border-border" />
														)}
													</div>
												))}
											</div>
											{uploadLogs.length === 0 && (
												<div className="flex flex-col justify-center items-center pt-28 w-full h-96">
													No csv found
												</div>
											)}
										</div>
									)}
								</div>
							</TabsContent>
							<TabsContent value="failed">
								<div className="flex flex-col gap-4 pt-10 w-full">
									<div className="flex justify-between items-center w-full">
										<p className="text-secondary text-xs">User names</p>
										<Button
											disabled={fetchingDownloadCsvUploadData}
											onClick={downloadCsv}
											className="flex items-center bg-transparent font-semibold underline"
										>
											<DownloadIcon2 />
											Download CSV
										</Button>
									</div>
									{fetchingAllUploadLogs ? (
										<div className="flex flex-col justify-center items-center pt-28 w-full h-96">
											<Spinner />
										</div>
									) : (
										<div className="flex flex-col w-full">
											<ScrollArea className="flex flex-col gap-8 h-72 overflow-y-scroll">
												{uploadLogs.map((uploadLog, index) => (
													<div key={uploadLog.id} className="flex flex-col">
														<div className="flex justify-between items-center pt-5">
															<p>{uploadLog.handle}</p>
														</div>
														{uploadLogs.length - 1 !== index && (
															<div className="py-4 border-b border-border" />
														)}
													</div>
												))}
											</ScrollArea>
											{uploadLogs.length === 0 && (
												<div className="flex flex-col justify-center items-center pt-28 w-full h-96">
													No csv found
												</div>
											)}
										</div>
									)}
									{fetchingAllUploadLogs ? (
										""
									) : (
										<RetryAll
											className="bottom-3 absolute border-primarybuttonborder bg-primarybuttonbg py-6 w-full text-black"
											csv={csvData as ICSVUpload}
										/>
									)}
								</div>
							</TabsContent>
						</Tabs>
					</div>
				</div>
			)}
		</div>
	);
};
